import { useEffect, useState } from "react";
import { useLoaderData, Link } from "react-router-dom";
import { CRow, CCol, CCard, CCardBody, CCardHeader, CButton, CSpinner, CAlert } from '@coreui/react';
import {
    FaChartLine,
    FaShoppingCart,
    FaBoxOpen,
    FaTags,
    FaNewspaper,
    FaUsers,
    FaDownload,
    FaUpload,
    FaSync,
} from "react-icons/fa";
import axios from "axios";
import AxiosInstance from "src/components/Axios";

const SYNC_URL = process.env.REACT_APP_SYNC_URL; // https://api.felso.ua
const MY_SYNC_URL = process.env.REACT_APP_SERVER_URL; // https://api.felso.com.ua

export async function LoaderHome() {
    return {};
}

const SyncBlock = ({ title, step1Label, step2Label, step1Desc, step2Desc, onBackup, onRestore, backupStatus, restoreStatus, backupMsg, restoreMsg, backupColor, restoreColor }) => (
    <CCard className="border-0 shadow-sm mb-4">
        <CCardHeader className="bg-transparent fw-bold border-bottom-0 pb-0">{title}</CCardHeader>
        <CCardBody>
            <CRow className="g-3">
                <CCol xs={12} md={6}>
                    <CCard className="border h-100">
                        <CCardBody className="d-flex flex-column gap-2">
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <FaDownload className={`text-${backupColor}`} />
                                <strong>{step1Label}</strong>
                            </div>
                            <p className="text-muted small mb-2">{step1Desc}</p>
                            {backupStatus === 'ok'    && <CAlert color="success" className="py-2 mb-2">{backupMsg || 'Бекап створено!'}</CAlert>}
                            {backupStatus === 'error' && <CAlert color="danger"  className="py-2 mb-2">{backupMsg}</CAlert>}
                            <CButton color={backupColor} disabled={backupStatus === 'loading'} onClick={onBackup}>
                                {backupStatus === 'loading' ? <><CSpinner size="sm" className="me-2" />Вивантажую...</> : 'Крок 1: Вивантажити'}
                            </CButton>
                        </CCardBody>
                    </CCard>
                </CCol>
                <CCol xs={12} md={6}>
                    <CCard className="border h-100">
                        <CCardBody className="d-flex flex-column gap-2">
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <FaUpload className={`text-${restoreColor}`} />
                                <strong>{step2Label}</strong>
                            </div>
                            <p className="text-muted small mb-2">{step2Desc}</p>
                            {restoreStatus === 'ok'    && <CAlert color="success" className="py-2 mb-2">{restoreMsg || 'Відновлено!'}</CAlert>}
                            {restoreStatus === 'error' && <CAlert color="danger"  className="py-2 mb-2">{restoreMsg}</CAlert>}
                            <CButton color={restoreColor} disabled={restoreStatus === 'loading'} onClick={onRestore}>
                                {restoreStatus === 'loading' ? <><CSpinner size="sm" className="me-2" />Відновлюю...</> : 'Крок 2: Відновити'}
                            </CButton>
                        </CCardBody>
                    </CCard>
                </CCol>
            </CRow>
        </CCardBody>
    </CCard>
);

const Home = () => {
    const [stats, setStats] = useState(null);

    // Синхронізація → felso.UA (backup від felso.com.ua, restore в felso.ua)
    const [uaBackupStatus,   setUaBackupStatus]   = useState(null);
    const [uaRestoreStatus,  setUaRestoreStatus]  = useState(null);
    const [uaBackupMsg,      setUaBackupMsg]      = useState('');
    const [uaRestoreMsg,     setUaRestoreMsg]     = useState('');

    // Синхронізація → felso.COM.UA (backup від felso.ua, restore в felso.com.ua)
    const [comBackupStatus,  setComBackupStatus]  = useState(null);
    const [comRestoreStatus, setComRestoreStatus] = useState(null);
    const [comBackupMsg,     setComBackupMsg]     = useState('');
    const [comRestoreMsg,    setComRestoreMsg]    = useState('');

    useEffect(() => {
        AxiosInstance.get('/dashboard/stats').then(r => setStats(r.data)).catch(() => {});
    }, []);

    // Виклик api.felso.UA (bearer з localStorage, без AxiosInstance бо інший baseURL)
    const callFelsoUa = async (domen, path, setStatus, setMsg) => {
        setStatus('loading');
        setMsg('');
        try {
            const token = localStorage.getItem('user');
            const res = await axios.get(`${domen}/${path}`, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 120000,
            });
            setStatus('ok');
            const tables = res.data?.tables ?? [];
            setMsg(tables.length ? `Таблиці: ${tables.join(', ')}` : 'Виконано');
        } catch (err) {
            setStatus('error');
            setMsg(err.response?.data?.error ?? err.response?.data?.message ?? err.message ?? 'Невідома помилка');
        }
    };

    // Виклик api.felso.COM.UA (AxiosInstance вже має baseURL + bearer)
    const callFelsoCom = async (path, setStatus, setMsg) => {
        setStatus('loading');
        setMsg('');
        try {
            const res = await AxiosInstance.get(`/${path}`);
            setStatus('ok');
            const tables = res.data?.tables ?? [];
            setMsg(tables.length ? `Таблиці: ${tables.join(', ')}` : 'Виконано');
        } catch (err) {
            setStatus('error');
            setMsg(err.response?.data?.error ?? err.response?.data?.message ?? err.message ?? 'Невідома помилка');
        }
    };

    const dashboardItems = [
        { title: "Клієнти",      icon: <FaChartLine size={30} className="text-primary mb-3" />,   link: "/statistics", desc: "Статистика та звіти",        color: "primary",   count: stats?.accounts,   unit: "осіб" },
        { title: "Замовлення",   icon: <FaShoppingCart size={30} className="text-success mb-3" />, link: "/pre-orders", desc: "Управління замовленнями",     color: "success",   count: stats?.orders,     unit: "шт.", subCount: stats ? { new: stats.orders_new, completed: stats.orders_completed } : null },
        { title: "Товари",       icon: <FaBoxOpen size={30} className="text-warning mb-3" />,      link: "/products",   desc: "Список усіх товарів",         color: "warning",   count: stats?.products,   unit: "шт." },
        { title: "Категорії",    icon: <FaTags size={30} className="text-info mb-3" />,             link: "/category",   desc: "Структура каталогу",          color: "info",      count: stats?.categories, unit: "шт." },
        { title: "Новини",       icon: <FaNewspaper size={30} className="text-danger mb-3" />,     link: "/news",       desc: "Блог та статті",              color: "danger",    count: stats?.news,       unit: "статей" },
        { title: "Користувачі",  icon: <FaUsers size={30} className="text-secondary mb-3" />,      link: "/users",      desc: "Адміністратори системи",      color: "secondary", count: stats?.users,      unit: "осіб" },
    ];

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0"><strong>Вітаємо у панелі керування!</strong></h4>
            </div>

            <CRow>
                {dashboardItems.map((item, index) => (
                    <CCol xs={12} sm={6} lg={4} key={index} className="mb-4">
                        <CCard className="h-100 shadow-sm border-0 text-center hover-elevate">
                            <CCardBody className="d-flex flex-column align-items-center justify-content-center p-4">
                                {item.icon}
                                <h5 className="card-title fw-bold mb-2">{item.title}</h5>
                                {stats !== null ? (
                                    <>
                                        <h3 className={`text-${item.color} fw-bolder mb-1`}>
                                            {item.count} <span className="fs-6 text-muted fw-normal">{item.unit}</span>
                                        </h3>
                                        {item.subCount && (
                                            <div className="d-flex justify-content-center gap-3 small text-muted mb-3">
                                                <span>Нових: <strong className="text-secondary">{item.subCount.new}</strong></span>
                                                <span>|</span>
                                                <span>Виконаних: <strong className="text-secondary">{item.subCount.completed}</strong></span>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="spinner-border spinner-border-sm text-secondary mb-2" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                )}
                                <p className={`text-muted small ${item.subCount ? 'mb-4 mt-0' : 'mb-4 mt-2'}`}>{item.desc}</p>
                                <Link to={item.link} className="mt-auto text-decoration-none w-100">
                                    <CButton color={item.color} variant="outline" className="w-100 fw-semibold rounded-pill">Перейти</CButton>
                                </Link>
                            </CCardBody>
                        </CCard>
                    </CCol>
                ))}
            </CRow>

            <hr className="my-4" />

            <div className="d-flex align-items-center gap-2 mb-3">
                <FaSync className="text-secondary" />
                <h5 className="mb-0 fw-bold">Синхронізація товарів</h5>
            </div>

            {/* felso.com.ua → felso.ua */}
            <SyncBlock
                title="felso.com.ua → felso.ua"
                step1Label="Вивантажити з felso.com.ua"
                step2Label="Завантажити на felso.ua"
                step1Desc="Бекапить таблиці товарів з бази felso.com.ua у файли на сервері felso.ua."
                step2Desc="Видаляє дані в felso.ua і відновлює з бекап-файлів. Виконувати після кроку 1."
                backupColor="primary"
                restoreColor="success"
                backupStatus={uaBackupStatus}
                restoreStatus={uaRestoreStatus}
                backupMsg={uaBackupMsg}
                restoreMsg={uaRestoreMsg}
                onBackup={() => callFelsoUa(MY_SYNC_URL, 'api/backup_db', setUaBackupStatus, setUaBackupMsg)}
                onRestore={() => callFelsoUa(SYNC_URL, 'api/restore', setUaRestoreStatus, setUaRestoreMsg)}
            />

            {/* felso.ua → felso.com.ua */}
            {/* <SyncBlock
                title="felso.ua → felso.com.ua"
                step1Label="Вивантажити з felso.ua"
                step2Label="Завантажити на felso.com.ua"
                step1Desc="Бекапить таблиці товарів з бази felso.ua (api_felso_brand) у файли на сервері felso.com.ua."
                step2Desc="Видаляє дані в felso.com.ua і відновлює з бекап-файлів. Виконувати після кроку 1."
                backupColor="warning"
                restoreColor="danger"
                backupStatus={comBackupStatus}
                restoreStatus={comRestoreStatus}
                backupMsg={comBackupMsg}
                restoreMsg={comRestoreMsg}
                onBackup={() => callFelsoCom('backup_db', setComBackupStatus, setComBackupMsg)}
                onRestore={() => callFelsoCom('restore', setComRestoreStatus, setComRestoreMsg)}
            /> */} 
        </div>
    );
};

export default Home;
